<template>
  <modal :show="modalVisible" size="lg" @close="closeModal">
    <div class="d-flex justify-content-between align-items-center ml-2 mr-2">
      <h2 class="mb-0">Firmware Update</h2>
      <button
        type="button"
        class="close"
        aria-label="Close"
        @click="closeModal"
        style="font-size:1.5rem;line-height:1;background:none;border:none;cursor:pointer;color:#6c757d;padding:0 4px;"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="p-2">
      <!-- Device Info -->
      <div class="card bg-secondary border shadow-none mb-2">
        <div class="card-body py-2 px-3">
          <div class="d-flex flex-wrap" style="gap:8px;">
            <div class="d-flex align-items-center mr-3">
              <i class="fas fa-microchip text-info mr-2"></i>
              <div>
                <small class="text-muted font-weight-bold d-block" style="font-size:0.65rem;">Device ID</small>
                <small class="text-dark font-weight-bold">{{ deviceId }}</small>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <i class="fas fa-code-branch text-info mr-2"></i>
              <div>
                <small class="text-muted font-weight-bold d-block" style="font-size:0.65rem;">Current Firmware</small>
                <small class="text-dark font-weight-bold">{{ currentFwVersion }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
  <div v-if="loadingSession" class="text-center py-4">
    <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
    <p class="mt-2 text-muted">Loading update status…</p>
  </div>
      <!-- PHASE 3: COMPLETED / FAILED / CANCELLED (MUST BE FIRST) -->
      <div v-if="otaSession && (otaSession.status === 'completed' || otaSession.status === 'failed' || otaSession.status === 'cancelled')">
        <div class="card shadow mb-2" :class="{
          'bg-success text-white': otaSession.status === 'completed',
          'bg-danger text-white':  otaSession.status === 'failed',
          'bg-secondary text-white': otaSession.status === 'cancelled'
        }">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center">
  <div>
    <i :class="['fas', 'mr-2', {
      'fa-check-circle':        otaSession.status === 'completed',
      'fa-exclamation-triangle': otaSession.status === 'failed',
      'fa-ban':                 otaSession.status === 'cancelled'
    }]"></i>
    <strong>Update {{ otaSession.status }}</strong>
  </div>
  <div>
    <!-- Show "Try Again" only for failed or cancelled -->
    <base-button
      v-if="otaSession.status === 'failed' || otaSession.status === 'cancelled'"
      variant="outline-light"
      size="sm"
      class="mr-2"
      @click="resetSessionAndRetry"
    >
      <i class="fas fa-redo-alt mr-1"></i> Try Again
    </base-button>
    <base-button variant="outline-light" size="sm" @click="closeModal">Close</base-button>
  </div>
</div>
            <div class="mt-2" v-if="otaSession.status === 'completed'">
            <div>New firmware version: <strong>{{ otaSession.fw_version_after || currentFwVersion }}</strong></div>
              <div class="small">Previous version: {{ otaSession.fw_version_before }}</div>
            </div>
            <div class="mt-2" v-if="otaSession.status === 'failed'">
              <div>Update timed out or device didn't report new version.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- PHASE 2: OTA PROGRESS (SESSION IN PROGRESS) -->
      <div v-else-if="otaSession && otaSession.status === 'in_progress'">
        <div class="card shadow mb-2">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <i class="fas fa-sync-alt fa-spin mr-2 text-primary"></i>
                <strong>OTA Update in Progress</strong>
              </div>
               <base-button
          variant="outline-danger"
          size="sm"
          @click="cancelOta"
          :disabled="cancelling"
        >
          <i class="fas fa-times mr-1"></i> Cancel
        </base-button>
          
            </div>

            <div class="progress mb-3" style="height: 8px;">
              <div
                class="progress-bar"
                :class="steps.completed ? 'bg-success' : 'progress-bar-striped progress-bar-animated bg-primary'"
                style="width: 100%;"
              ></div>
            </div>

            <div class="small text-muted mb-2">
              <div><strong>Target:</strong> {{ otaSession.target_version }}</div>
              <div><strong>Current version:</strong> {{ currentFwVersion }}</div>
            </div>

            <div class="mt-2">
              <!-- Step 1 -->
              <div class="d-flex align-items-center mb-2">
                <i class="fas fa-check-circle text-success mr-2" v-if="steps.sent"></i>
                <i class="fas fa-circle-notch fa-spin text-primary mr-2" v-else></i>
                <span>1. Sending update command</span>
              </div>
              <!-- Step 2 -->
              <div class="d-flex align-items-center mb-2">
                <i class="fas fa-check-circle text-success mr-2" v-if="steps.downloadCompleted"></i>
                <i class="fas fa-circle-notch fa-spin text-primary mr-2" v-else-if="steps.downloading"></i>
                <i class="far fa-circle text-muted mr-2" v-else></i>
                <span>2. Device downloading firmware</span>
              </div>
              <!-- Step 3: WiFi firmware (conditional) -->
              <div v-if="otaSession.has_wifi" class="d-flex align-items-center mb-2">
                <i class="fas fa-check-circle text-success mr-2" v-if="steps.wifiCompleted"></i>
                <i class="fas fa-circle-notch fa-spin text-primary mr-2" v-else-if="steps.sendingWifi"></i>
                <i class="far fa-circle text-muted mr-2" v-else></i>
                <span>3. Sending WiFi firmware</span>
              </div>
           
              <!-- Step 5: Verifying -->
              <div class="d-flex align-items-center">
                <i class="fas fa-check-circle text-success mr-2" v-if="steps.completed"></i>
                <i class="fas fa-circle-notch fa-spin text-primary mr-2" v-else-if="steps.rebooting"></i>
                <i class="far fa-circle text-muted mr-2" v-else></i>
                <span>5. Verifying new version</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PHASE 1: FIRMWARE SELECTION (DEFAULT – NO ACTIVE SESSION) -->
      <div v-else>
        <!-- State warning -->
        <b-alert v-if="deviceState !== 'UNALLOCATED' && deviceState !== 'ERROR'" show variant="warning" class="mb-2">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Device must be in <strong>Unallocated</strong> or <strong>Error</strong> state to update firmware.
          Current state: <strong>{{ deviceState }}</strong>
        </b-alert>

        <!-- Firmware list -->
        <div class="card shadow mb-2">
          <div class="card-header py-2 bg-light font-weight-bold">
            <i class="fas fa-cloud-download-alt mr-2"></i>Select Firmware
          </div>
          <div class="card-body p-0">
            <div class="list-group list-group-flush">
              <!-- Latest version prominently -->
              <div v-if="latestFirmware" class="list-group-item list-group-item-action flex-column align-items-start bg-light">
                <div class="d-flex w-100 justify-content-between align-items-center">
                  <div>
                    <i class="fas fa-star text-warning mr-2"></i>
                    <strong>{{ latestFirmware.version }}</strong>
                    <span class="badge badge-success ml-2">Latest</span>
                  </div>
                  <base-button
                    size="sm"
                    type="primary"
                    @click="selectFirmware(latestFirmware)"
                    :disabled="!canStartOta || selecting"
                  >
                    <i class="fas fa-play mr-1"></i> Update to this version
                  </base-button>
                </div>
                <small class="text-muted">Uploaded: {{ formatDate(latestFirmware.release_date) }}</small>
                <div v-if="latestFirmware.description" class="small text-muted mt-1">{{ latestFirmware.description }}</div>
              </div>

              <!-- Older versions collapsible -->
              <div v-if="olderFirmwares.length" class="list-group-item">
                <div class="d-flex justify-content-between align-items-center cursor-pointer" @click="toggleOlderVersions">
                  <span><i class="fas fa-history mr-2"></i> Older versions ({{ olderFirmwares.length }})</span>
                  <i class="fas fa-chevron-down" :class="{ 'fa-rotate-180': olderExpanded }"></i>
                </div>
                <b-collapse v-model="olderExpanded">
                  <div v-for="fw in olderFirmwares" :key="fw.id" class="d-flex justify-content-between align-items-center mt-2 border-top pt-2">
                    <div>
                      <span>{{ fw.version }}</span>
                      <span v-if="fw.version === currentFwVersion" class="badge badge-secondary ml-2">already installed</span>
                      <div class="small text-muted">{{ formatDate(fw.release_date) }}</div>
                    </div>
                    <base-button
                      size="sm"
                      type="outline-primary"
                      @click="selectFirmware(fw)"
                      :disabled="!canStartOta || selecting || fw.version === currentFwVersion"
                    >
                      Update
                    </base-button>
                  </div>
                </b-collapse>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <base-button variant="link" @click="closeModal">
        <i class="fas fa-times mr-1"></i> Close
      </base-button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/views/components/theme/Modal";
import BaseButton from "@/views/components/theme/BaseButton";

export default {
  name: "ModalFirmwareOTA",
  components: { Modal, BaseButton },
  props: {
    deviceId:         { type: String, required: true },
    entity:           { type: String, required: true },
    currentFwVersion: { type: String, default: "" },
    deviceState:      { type: String, default: "" },
  },
  data() {
    return {
      modalVisible: false,
      allFirmware:    [],
      latestFirmware: null,
      olderFirmwares: [],
      olderExpanded:  false,
      selecting:      false,
      cancelling:     false,
      otaSession:     null,
      pollTimer:      null,
      downloadingStartTime: null,
          loadingSession: true,

      // All step display state lives in one flat object — rebuilt by applyState() on every poll
      steps: {
        sent:              false,
        downloading:       false,
        downloadCompleted: false,
        rebooting:         false,
        rebootCompleted:   false,
        sendingWifi:       false,
        wifiCompleted:     false,
        completed:         false,
      },
    };
  },
  computed: {
    canStartOta() {
      return this.deviceState === 'UNALLOCATED' || this.deviceState === 'ERROR';
    },
  },
 async mounted() {
   

  this.modalVisible = true;
  if (!this.deviceId) {
    this.loadingSession = false;
    return;
  }

  // ✅ Fetch current session state before showing anything
  await this.poll();
  this.loadingSession = false;

  // Now load firmware list and start background polling
  this.loadFirmwareList();
  this.startPolling();
  this.syncGitlab(); 

  this._onVisible = () => {
    if (document.visibilityState === 'visible') this.poll();
  };
  document.addEventListener('visibilitychange', this._onVisible);
},
  beforeUnmount() {
    this.stopPolling();
    if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
  },
  methods: {
    // -------------------------------------------------------------------------
    // Core state rebuilder — mirrors ModalProvision's applyState()
    // Called on every poll response; never mutates steps partially.
    // -------------------------------------------------------------------------
    async syncGitlab() {
  try {
     const resp = await this.$http.post(  // ← add "const resp ="
      `/tenant/${this.entity}/firmware/sync-gitlab?device_type=CC2`
    );
        console.log('[GitLab sync]', resp.data); // ← add this

    // refresh list after sync in case new versions were pulled
    await this.loadFirmwareList();
  } catch (e) {
    // silent — sync failing shouldn't block the modal
    console.warn('GitLab sync skipped:', e?.response?.data || e.message);
  }
},
   applyState(data) {
  if (!data || data.status === 'no_active_ota') return;

  this.otaSession = {
    ...data,
    fw_version_after: data.fw_version_after || data.current_fw_version
  };

  if (data.status === 'completed') {
    this.steps = {
      sent:              true,
      downloading:       false,
      downloadCompleted: true,
      rebooting:         false,
      rebootCompleted:   true,
      sendingWifi:       false,
      wifiCompleted:     !!data.has_wifi,
      completed:         true,
    };
    this.stopPolling();
    this.$emit('ota-completed');

  } else if (data.status === 'failed' || data.status === 'cancelled') {
    this.steps = { ...this.steps, sent: true };
    this.stopPolling();

  } else if (data.status === 'in_progress') {
    this.steps.sent = true;

    if (data.has_wifi && data.wifi_sent) {
      this.steps.downloading = false;
      this.steps.downloadCompleted = true;
    }

    if (!this.steps.downloading && !this.steps.downloadCompleted) {
      this.steps.downloading = true;
      this.downloadingStartTime = this.downloadingStartTime || Date.now();
    }

    if (data.has_wifi && !data.wifi_sent && !this.steps.wifiCompleted) {
      this.steps.sendingWifi = true;
    }
    if (data.has_wifi && data.wifi_sent) {
      this.steps.sendingWifi = false;
      this.steps.wifiCompleted = true;
    }

    if (
      this.steps.downloading &&
      !this.steps.downloadCompleted &&
      !this.steps.rebooting &&
      this.downloadingStartTime &&
      (Date.now() - this.downloadingStartTime) > 5000 &&
      data.current_fw_version === data.fw_version_before
    ) {
      this.steps.downloading       = false;
      this.steps.downloadCompleted = true;
      this.steps.rebooting         = true;
    }
  }
},

resetSessionAndRetry() {
  // Clear the local session so template falls back to firmware selection
  this.otaSession = null;
  // Reset step indicators
  this.steps = {
    sent: false,
    downloading: false,
    downloadCompleted: false,
    rebooting: false,
    rebootCompleted: false,
    sendingWifi: false,
    wifiCompleted: false,
    completed: false,
  };
  // Stop any active polling
  this.stopPolling();
  // Ensure firmware list is loaded
  this.loadFirmwareList();
  // Set loading flag to false (already false from previous session)
  this.loadingSession = false;
},
    // -------------------------------------------------------------------------
    // Polling — same structure as ModalProvision
    // -------------------------------------------------------------------------
    startPolling() {
      this.stopPolling();
      this.poll();
      this.pollTimer = setInterval(() => this.poll(), 3000);
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
   
async poll() {
  if (!this.deviceId) return;
  try {
    const resp = await this.$http.get(
      `/tenant/${this.entity}/firmware/${this.deviceId}/ota/status`
    );
   // console.log('OTA POLL RESPONSE:', JSON.stringify(resp.data));

    if (resp.data.status === 'no_active_ota') {
      this.stopPolling();
      return;
    }

    this.applyState(resp.data);

  } catch (err) {
    if (err.response?.status === 404) {
      this.stopPolling();
    } else {
      console.error('OTA poll error', err);
    }
  }
},

    // -------------------------------------------------------------------------
    // Start OTA
    // -------------------------------------------------------------------------
   async loadFirmwareList() {
  try {
    const resp = await this.$http.get(
      `/tenant/${this.entity}/firmware/list/CC2?include_all=true`
    );
    const allFirmware = resp.data || [];

    // Deduplicate: keep only the most specific level for each version
    const levelPriority = { group: 3, site: 2, entity: 1 };
    const deduped = {};
    allFirmware.forEach(fw => {
      const existing = deduped[fw.version];
      if (!existing || levelPriority[fw.level] > levelPriority[existing.level]) {
        deduped[fw.version] = fw;
      }
    });

    const uniqueFirmware = Object.values(deduped);

    // Sort newest first (using your existing compareVersions function)
    this.allFirmware = uniqueFirmware.sort((a, b) =>
      this.compareVersions(b.version, a.version)
    );

    if (this.allFirmware.length) {
      this.latestFirmware = this.allFirmware[0];
      this.olderFirmwares = this.allFirmware.slice(1);
    } else {
      this.latestFirmware = null;
      this.olderFirmwares = [];
    }
  } catch (e) {
    console.error("Failed to load firmware list", e);
  }
},
    compareVersions(v1, v2) {
      const toNum = v => {
        const [main, tag] = v.split('-');
        const [major, minor, patch] = main.split('.').map(Number);
        const tagOrder = { rel: 4, rc: 3, beta: 2, dev: 1 };
        return [major, minor, patch, tagOrder[tag] || 0];
      };
      const a = toNum(v1), b = toNum(v2);
      for (let i = 0; i < 4; i++) {
        if (a[i] !== b[i]) return a[i] - b[i];
      }
      return 0;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    },
    toggleOlderVersions() {
      this.olderExpanded = !this.olderExpanded;
    },
    async selectFirmware(fw) {
      if (!this.canStartOta) {
        this.$notify({ title: "Cannot start", message: `Device must be UNALLOCATED or ERROR. Current: ${this.deviceState}`, type: "warning" });
        return;
      }
      this.selecting = true;
      try {
        const resp = await this.$http.post(
          `/tenant/${this.entity}/firmware/${this.deviceId}/ota/start`,
          { firmware_id: fw.id }
        );
        // Seed local session immediately so UI switches to progress view
        this.otaSession = {
          session_id:       resp.data.session_id,
          target_version:   resp.data.target_version,
          status:           'in_progress',
          fw_version_before: this.currentFwVersion,
          fw_version_after:  '',
          current_fw_version: this.currentFwVersion,
          has_wifi:         false,
          wifi_sent:        false,
          started_at:       new Date().toISOString(),
        };
        this.steps = {
          sent:              true,
          downloading:       true,
          downloadCompleted: false,
          rebooting:         false,
          rebootCompleted:   false,
          sendingWifi:       false,
          wifiCompleted:     false,
          completed:         false,
        };
        this.downloadingStartTime = Date.now();
        this.startPolling();
      } catch (err) {
        console.error("OTA start failed", err);
        this.$notify({ title: "Error", message: err.response?.data?.error || err.message, type: "error" });
      } finally {
        this.selecting = false;
      }
    },

    // -------------------------------------------------------------------------
    // Cancel OTA
    // -------------------------------------------------------------------------
    async cancelOta() {
  if (this.cancelling) return;

  this.cancelling = true;
  try {
    await this.$http.post(
      `/tenant/${this.entity}/firmware/${this.deviceId}/ota/cancel`
    );
    this.$notify({
      title: "Cancelled",
      message: "OTA update cancelled. You can start again.",
      type: "info",
    });

    // ✅ Reset everything so the user can immediately retry
    this.resetSessionAndRetry();
  } catch (err) {
    console.error("Cancel failed", err);
    this.$notify({
      title: "Error",
      message: err.response?.data?.error || err.message,
      type: "error",
    });
  } finally {
    this.cancelling = false;
  }
},
    closeModal() {
      this.stopPolling();
      this.$emit("close-modal");
    },
  },
  watch: {
  deviceId: {
    immediate: true,
    handler(newVal) {
      if (newVal && this.modalVisible) {
        this.loadFirmwareList();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    }
  }
},
};
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>